import { Minus as RemoveIcon, Plus as AddIcon, Trash2 as DeleteOutline } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setSelectedProduct } from "../../../../api/reducers/selectedProductReducer";
import { Box, Typography } from "../../../../components/ui/LayoutPrimitives";
import { IconButton } from "../../../../components/ui/IconButton";
import { Tooltip } from "../../../../components/ui/Feedback";

const getPlainDescription = (description) => {
  if (!description) return "No description available";

  if (typeof document === "undefined") {
    return description.replace(/<[^>]*>/g, "");
  }

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = description;
  return tempDiv.textContent || tempDiv.innerText || "";
};

const AvikarCartSkeleton = ({
  item,
  quantity,
  currency,
  handleRemove,
  setIncrease,
  setDecrease,
  id,
}) => {
  const nav = useNavigate();
  const dispatch = useDispatch();
  const numericPrice = Number(item?.price?.priceAmt);
  const unitPrice = Number.isFinite(numericPrice) ? numericPrice : 0;
  const numericQuantity = Number(quantity);
  const safeQuantity = Number.isFinite(numericQuantity) ? numericQuantity : 1;

  const handleProductClick = () => {
    dispatch(setSelectedProduct(item));
    nav("/product-info");
  };

  const handleDecreaseClick = () => {
    setDecrease(id, item?.price?.priceAmt);
  };

  const handleIncreaseClick = () => {
    setIncrease(id, item?.price?.priceAmt);
  };

  const handleDeleteClick = () => {
    handleRemove(id);
  };

  const plainDescription = getPlainDescription(item?.description);
  const descriptionPreview = plainDescription.slice(0, 80) + (plainDescription.length > 80 ? "..." : "");

  return (
    <Box className="flex flex-col md:flex-row gap-4 p-4 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200">
      {/* Product Image */}
      <Box
        className="w-full md:w-1/4 h-40 bg-gray-100 rounded-lg overflow-hidden cursor-pointer"
        onClick={handleProductClick}
      >
        <img
          loading="lazy"
          src={
            item.imageContents?.[0]?.displayLink ||
            "/luxury-placeholder.jpg"
          }
          alt={item?.name || "Product"}
          className="w-full h-full object-contain transition-transform"
        />
      </Box>

      {/* Product Details */}
      <Box className="flex flex-col flex-1">
        <Box className="flex justify-between items-start">
          <Box className="flex-1">
            <Typography
              variant="h6"
              className="font-medium text-gray-800 cursor-pointer hover:text-orange-500 transition-colors"
              onClick={handleProductClick}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {item?.name}
            </Typography>
            <Typography variant="body2" className="text-gray-500 mt-1">
              {descriptionPreview}
            </Typography>
          </Box>

          <Typography variant="h6" className="font-bold text-gray-800 ml-4">
            {currency}{(unitPrice * safeQuantity).toFixed(2)}
          </Typography>
        </Box>

        <Box className="mt-auto pt-4 flex flex-wrap justify-between items-center">
          {/* Quantity Controls */}
          <Box className="flex items-center gap-4">
            <Box className="flex items-center border border-gray-300 rounded-md">
              <IconButton
                aria-label="Decrease quantity"
                size="sm"
                variant="ghost"
                onClick={handleDecreaseClick}
                disabled={quantity <= 1}
              >
                <RemoveIcon className="h-4 w-4" />
              </IconButton>

              <Typography variant="body1" className="px-3 font-medium min-w-8 text-center">
                {quantity}
              </Typography>

              <IconButton
                aria-label="Increase quantity"
                size="sm"
                variant="ghost"
                onClick={handleIncreaseClick}
              >
                <AddIcon className="h-4 w-4" />
              </IconButton>
            </Box>

            {/* Unit Price */}
            <Typography variant="body2" className="text-gray-600">
              {currency}{unitPrice.toFixed(2)} each
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box className="flex items-center gap-2">
            {/* Delete Button */}
            <Tooltip content="Remove from cart">
              <IconButton
                aria-label="Remove from cart"
                onClick={handleDeleteClick}
                size="sm"
                variant="danger"
              >
                <DeleteOutline className="h-4 w-4" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AvikarCartSkeleton;
